import { DatabaseConflictError } from "../lib/database";
import { inputFollowUps, inputOrders } from "../lib/inputCompatibility";
import type {
  Activity,
  ActivityChannelInput,
  ActivityEditInput,
  AttachmentInput,
  AuditEvent,
  AuditOperation,
  AuditTableName,
  CaptureBundleInput,
  CaptureBundleResult,
  Contact,
  ContactDirectoryEntry,
  ContactInput,
  CrmAttachment,
  CrmInput,
  DedupeCandidate,
  DeleteRecordType,
  FollowUp,
  FollowUpDetail,
  FollowUpEditInput,
  FollowUpLifecycleInput,
  Opportunity,
  OpportunityInput,
  Order,
  OrderEditInput,
  Quote,
  QuoteInput,
  SampleRequest,
  SampleRequestInput,
  Vendor,
  VendorCrmProfileInput,
  VendorInput,
  VendorMatch,
  VendorMatchInput,
} from "../types";
import {
  demoActivities,
  demoAdamProfile,
  demoAdamUserId,
  demoAttachments,
  demoAuditEvents,
  demoContacts,
  demoDedupeCandidates,
  demoFollowUps,
  demoOpportunities,
  demoOrders,
  demoOrganizationId,
  demoQuotes,
  demoSampleRequests,
  demoVendors,
  makeDemoDashboard,
  makeDemoVendorDetail,
} from "./demoData";
import type { Repository } from "./Repository";

export class DemoRepository implements Repository {
  async getCurrentRole() {
    return "admin" as const;
  }

  async getDashboard() {
    return makeDemoDashboard();
  }

  async searchVendors(query: string) {
    const needle = query.trim().toLowerCase();
    if (!needle) return [...demoVendors].sort(compareLastActivity);
    return demoVendors
      .filter((vendor) =>
        [
          vendor.trading_name,
          vendor.legal_name,
          vendor.abn,
          vendor.location_name,
          vendor.suburb,
        ].some((value) => value?.toLowerCase().includes(needle)),
      )
      .sort(compareLastActivity);
  }

  async searchContacts(query: string, limit = 8): Promise<ContactDirectoryEntry[]> {
    const needle = query.trim().toLocaleLowerCase("en-AU");
    if (!needle) return [];
    return demoContacts
      .flatMap((contact) => {
        const vendor = demoVendors.find(
          (candidate) =>
            candidate.id === contact.vendor_id && candidate.organization_id === demoOrganizationId,
        );
        if (!vendor) return [];
        const matches = contact.name.toLocaleLowerCase("en-AU").includes(needle);
        return matches ? [{ contact, vendor }] : [];
      })
      .slice(0, Math.max(1, limit));
  }

  async matchVendors(input: VendorMatchInput): Promise<VendorMatch[]> {
    const name = input.name.trim().toLowerCase();
    const location = input.locationText?.trim().toLowerCase() ?? "";
    return demoVendors
      .map((vendor) => {
        const vendorName = vendor.trading_name.toLowerCase();
        const nameScore =
          vendorName === name
            ? 1
            : vendorName.includes(name) || name.includes(vendorName)
              ? 0.9
              : tokenSimilarity(vendorName, name);
        const vendorLocation = [
          vendor.location_name,
          vendor.address_line_1,
          vendor.suburb,
          vendor.state,
          vendor.postcode,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const locationScore = location
          ? vendorLocation.includes(location)
            ? 1
            : tokenSimilarity(vendorLocation, location)
          : 0;
        const distance =
          input.latitude != null &&
          input.longitude != null &&
          vendor.latitude != null &&
          vendor.longitude != null
            ? distanceBetween(input.latitude, input.longitude, vendor.latitude, vendor.longitude)
            : null;
        const proximityScore =
          distance == null
            ? 0
            : distance <= 0.25
              ? 1
              : distance <= 1
                ? 0.9
                : distance <= 5
                  ? 0.7
                  : 0.1;
        const contextScore = location ? locationScore : proximityScore;
        const hasContext = Boolean(location) || (input.latitude != null && input.longitude != null);
        const matchScore =
          !name && hasContext
            ? contextScore
            : hasContext
              ? nameScore * 0.75 + contextScore * 0.25
              : nameScore;
        return {
          ...vendor,
          match_score: matchScore,
          distance_km: distance,
        };
      })
      .filter((vendor) => !name || vendor.match_score >= 0.12)
      .sort(
        (first, second) =>
          second.match_score - first.match_score ||
          (first.distance_km ?? Number.POSITIVE_INFINITY) -
            (second.distance_km ?? Number.POSITIVE_INFINITY),
      )
      .slice(0, input.limit ?? 8);
  }

  async getVendor(id: string) {
    const detail = makeDemoVendorDetail(id);
    if (!detail) throw new Error("Account not found");
    return detail;
  }

  async getOrder(id: string) {
    const order = demoOrders.find((item) => item.id === id);
    if (!order) throw new Error("Order not found");
    const vendor = demoVendors.find((item) => item.id === order.vendor_id);
    if (!vendor) throw new Error("Order account not found");
    return {
      ...order,
      vendor: { id: vendor.id, trading_name: vendor.trading_name },
    };
  }

  async getFollowUp(id: string): Promise<FollowUpDetail> {
    const followUp = demoFollowUps.find((item) => item.id === id);
    if (!followUp) throw new Error("Follow-up not found");
    const account = demoVendors.find((item) => item.id === followUp.vendor_id);
    if (!account) throw new Error("Follow-up account not found");
    return {
      ...followUp,
      vendor: { id: account.id, trading_name: account.trading_name },
    };
  }

  async createVendor(input: VendorInput, contact?: ContactInput): Promise<Vendor> {
    const now = new Date().toISOString();
    const vendor: Vendor = {
      id: crypto.randomUUID(),
      organization_id: demoVendors[0]?.organization_id ?? crypto.randomUUID(),
      trading_name: input.trading_name,
      legal_name: input.legal_name || null,
      abn: input.abn || null,
      location_name: input.location_name || null,
      email: input.email || null,
      phone: input.phone || null,
      address_line_1: input.address_line_1 || null,
      address_line_2: input.address_line_2 || null,
      suburb: input.suburb || null,
      state: input.state || null,
      postcode: input.postcode || null,
      status: input.status,
      tags: [],
      notes: input.notes || null,
      last_activity_at: null,
      latitude: input.latitude ?? null,
      longitude: input.longitude ?? null,
      google_place_id: input.google_place_id ?? null,
      location_source: input.location_source ?? "manual",
      location_confirmed_at: input.location_source ? now : null,
      entity_type: "venue",
      parent_vendor_id: null,
      lifecycle_stage: input.status === "active" ? "customer" : "prospect",
      account_priority: "normal",
      contact_cadence_days: null,
      next_contact_at: null,
      owner_id: demoAdamUserId,
      row_version: 1,
      created_at: now,
      updated_at: now,
    };
    demoVendors.unshift(vendor);
    recordDemoAudit("vendors", vendor.id, "INSERT", null, snapshot(vendor));
    if (contact) {
      await this.saveContact(vendor.id, null, null, contact);
    }
    return vendor;
  }

  async updateVendor(id: string, expectedVersion: number, input: VendorInput): Promise<Vendor> {
    const vendor = demoVendors.find((item) => item.id === id);
    if (!vendor) throw new Error("Account not found");
    assertVersion(vendor, expectedVersion);
    const before = snapshot(vendor);
    Object.assign(vendor, {
      trading_name: input.trading_name,
      legal_name: input.legal_name || null,
      abn: input.abn || null,
      location_name: input.location_name || null,
      email: input.email || null,
      phone: input.phone || null,
      address_line_1: input.address_line_1 || null,
      address_line_2: input.address_line_2 || null,
      suburb: input.suburb || null,
      state: input.state || null,
      postcode: input.postcode || null,
      status: input.status,
      notes: input.notes || null,
      latitude: input.latitude ?? null,
      longitude: input.longitude ?? null,
      row_version: expectedVersion + 1,
      updated_at: new Date().toISOString(),
    });
    recordDemoAudit("vendors", vendor.id, "UPDATE", before, snapshot(vendor));
    return vendor;
  }

  async updateVendorCrmProfile(
    id: string,
    expectedVersion: number,
    input: VendorCrmProfileInput,
  ): Promise<Vendor> {
    const vendor = demoVendors.find((item) => item.id === id);
    if (!vendor) throw new Error("Account not found");
    assertVersion(vendor, expectedVersion);
    if (input.parent_vendor_id) {
      const parent = demoVendors.find((item) => item.id === input.parent_vendor_id);
      if (
        !parent ||
        parent.organization_id !== vendor.organization_id ||
        parent.entity_type !== "account"
      ) {
        throw new Error("A venue parent must be an account in the same organization");
      }
    }
    if (input.entity_type === "account" && input.parent_vendor_id) {
      throw new Error("An account cannot have a parent account");
    }
    if (
      input.entity_type === "venue" &&
      demoVendors.some((item) => item.parent_vendor_id === vendor.id)
    ) {
      throw new Error("An account with child venues cannot be converted to a venue");
    }
    const before = snapshot(vendor);
    Object.assign(vendor, input, {
      row_version: expectedVersion + 1,
      updated_at: new Date().toISOString(),
    });
    recordDemoAudit("vendors", vendor.id, "UPDATE", before, snapshot(vendor));
    return vendor;
  }

  async updateActivity(
    id: string,
    expectedVersion: number,
    input: ActivityEditInput,
  ): Promise<Activity> {
    const activity = demoActivities.find((item) => item.id === id);
    if (!activity) throw new Error("Activity not found");
    assertVersion(activity, expectedVersion);
    const before = snapshot(activity);
    Object.assign(activity, {
      ...input,
      details: input.details || null,
      outcome: input.outcome || null,
      next_action: input.next_action || null,
      row_version: expectedVersion + 1,
      updated_at: new Date().toISOString(),
    });
    recordDemoAudit("activities", activity.id, "UPDATE", before, snapshot(activity));
    return activity;
  }

  async updateActivityChannel(
    id: string,
    expectedVersion: number,
    input: ActivityChannelInput,
  ): Promise<Activity> {
    const activity = demoActivities.find((item) => item.id === id);
    if (!activity) throw new Error("Activity not found");
    assertVersion(activity, expectedVersion);
    const before = snapshot(activity);
    Object.assign(activity, input, {
      row_version: expectedVersion + 1,
      updated_at: new Date().toISOString(),
    });
    recordDemoAudit("activities", activity.id, "UPDATE", before, snapshot(activity));
    return activity;
  }

  async updateOrder(id: string, expectedVersion: number, input: OrderEditInput): Promise<Order> {
    const order = demoOrders.find((item) => item.id === id);
    if (!order) throw new Error("Order not found");
    assertVersion(order, expectedVersion);
    const before = snapshot(order);
    Object.assign(order, {
      ...input,
      quantity: input.quantity ?? null,
      unit: input.unit || null,
      amount: input.amount ?? null,
      expected_at: input.expected_at ?? null,
      row_version: expectedVersion + 1,
      updated_at: new Date().toISOString(),
    });
    recordDemoAudit("orders", order.id, "UPDATE", before, snapshot(order));
    return order;
  }

  async updateFollowUp(
    id: string,
    expectedVersion: number,
    input: FollowUpEditInput,
  ): Promise<FollowUp> {
    const followUp = demoFollowUps.find((item) => item.id === id);
    if (!followUp) throw new Error("Follow-up not found");
    assertVersion(followUp, expectedVersion);
    const before = snapshot(followUp);
    Object.assign(followUp, {
      ...input,
      completed_at: input.status === "completed" ? new Date().toISOString() : null,
      row_version: expectedVersion + 1,
      updated_at: new Date().toISOString(),
    });
    recordDemoAudit("follow_ups", followUp.id, "UPDATE", before, snapshot(followUp));
    return followUp;
  }

  async updateFollowUpLifecycle(
    id: string,
    expectedVersion: number,
    input: FollowUpLifecycleInput,
  ): Promise<FollowUp> {
    const followUp = demoFollowUps.find((item) => item.id === id);
    if (!followUp) throw new Error("Follow-up not found");
    assertVersion(followUp, expectedVersion);
    const before = snapshot(followUp);
    if (input.action === "complete") {
      followUp.status = "completed";
      followUp.completed_at = new Date().toISOString();
    } else if (input.action === "cancel") {
      followUp.status = "cancelled";
      followUp.completed_at = null;
    } else if (input.action === "reopen") {
      followUp.status = "open";
      followUp.completed_at = null;
    } else if (input.action === "snooze") {
      if (!input.due_at) throw new Error("A snooze date is required");
      followUp.status = "open";
      followUp.completed_at = null;
      followUp.due_at = input.due_at;
      followUp.snoozed_until = input.due_at;
      followUp.snooze_count = (followUp.snooze_count ?? 0) + 1;
    } else if (input.action === "assign") {
      if (!input.assigned_to) throw new Error("An owner is required");
      followUp.assigned_to = input.assigned_to;
    }
    followUp.row_version = expectedVersion + 1;
    followUp.updated_at = new Date().toISOString();
    recordDemoAudit("follow_ups", followUp.id, "UPDATE", before, snapshot(followUp));
    return followUp;
  }

  async saveOpportunity(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: OpportunityInput,
  ): Promise<Opportunity> {
    const existing = id ? demoOpportunities.find((item) => item.id === id) : null;
    const now = new Date().toISOString();
    const values = {
      ...input,
      estimated_value: input.estimated_value ?? null,
      currency: input.currency ?? "AUD",
      probability: input.probability ?? (input.stage === "won" ? 100 : 10),
      expected_close_date: input.expected_close_date ?? null,
      next_step: input.next_step ?? null,
      owner_id: input.owner_id ?? demoAdamUserId,
      closed_at: input.stage === "won" || input.stage === "lost" ? now : null,
      lost_reason: input.stage === "lost" ? (input.lost_reason ?? null) : null,
    };
    if (existing) {
      if (expectedVersion === null) throw new Error("A record version is required for updates");
      assertVersion(existing, expectedVersion);
      const before = snapshot(existing);
      Object.assign(existing, values, { row_version: expectedVersion + 1, updated_at: now });
      recordDemoAudit("opportunities", existing.id, "UPDATE", before, snapshot(existing));
      return existing;
    }
    requireDemoVendor(vendorId);
    const opportunity: Opportunity = {
      id: crypto.randomUUID(),
      organization_id: demoOrganizationId,
      vendor_id: vendorId,
      ...values,
      created_by: demoAdamUserId,
      updated_by: demoAdamUserId,
      row_version: 1,
      created_at: now,
      updated_at: now,
    };
    demoOpportunities.unshift(opportunity);
    recordDemoAudit("opportunities", opportunity.id, "INSERT", null, snapshot(opportunity));
    return opportunity;
  }

  async saveSampleRequest(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: SampleRequestInput,
  ): Promise<SampleRequest> {
    const existing = id ? demoSampleRequests.find((item) => item.id === id) : null;
    const now = new Date().toISOString();
    const wasSent = ["sent", "delivered", "feedback", "closed"].includes(input.status);
    const wasDelivered = ["delivered", "feedback", "closed"].includes(input.status);
    const values = {
      opportunity_id: input.opportunity_id ?? null,
      product_id: input.product_id ?? null,
      description: input.description,
      quantity: input.quantity ?? null,
      unit: input.unit ?? null,
      status: input.status,
      sent_at: wasSent ? now : null,
      delivered_at: wasDelivered ? now : null,
      feedback: input.feedback ?? null,
      owner_id: input.owner_id ?? demoAdamUserId,
    };
    if (existing) {
      if (expectedVersion === null) throw new Error("A record version is required for updates");
      assertVersion(existing, expectedVersion);
      const before = snapshot(existing);
      Object.assign(existing, values, { row_version: expectedVersion + 1, updated_at: now });
      recordDemoAudit("sample_requests", existing.id, "UPDATE", before, snapshot(existing));
      return existing;
    }
    requireDemoVendor(vendorId);
    const sample: SampleRequest = {
      id: crypto.randomUUID(),
      organization_id: demoOrganizationId,
      vendor_id: vendorId,
      ...values,
      created_by: demoAdamUserId,
      updated_by: demoAdamUserId,
      row_version: 1,
      created_at: now,
      updated_at: now,
    };
    demoSampleRequests.unshift(sample);
    recordDemoAudit("sample_requests", sample.id, "INSERT", null, snapshot(sample));
    return sample;
  }

  async saveQuote(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: QuoteInput,
  ): Promise<Quote> {
    const existing = id ? demoQuotes.find((item) => item.id === id) : null;
    const now = new Date().toISOString();
    const wasSent = ["sent", "accepted", "rejected", "expired"].includes(input.status);
    const values = {
      opportunity_id: input.opportunity_id ?? null,
      quote_reference: input.quote_reference ?? null,
      summary: input.summary,
      amount: input.amount ?? null,
      currency: input.currency ?? "AUD",
      status: input.status,
      valid_until: input.valid_until ?? null,
      sent_at: wasSent ? now : null,
      accepted_at: input.status === "accepted" ? now : null,
      owner_id: input.owner_id ?? demoAdamUserId,
    };
    if (existing) {
      if (expectedVersion === null) throw new Error("A record version is required for updates");
      assertVersion(existing, expectedVersion);
      const before = snapshot(existing);
      Object.assign(existing, values, { row_version: expectedVersion + 1, updated_at: now });
      recordDemoAudit("quotes", existing.id, "UPDATE", before, snapshot(existing));
      return existing;
    }
    requireDemoVendor(vendorId);
    const quote: Quote = {
      id: crypto.randomUUID(),
      organization_id: demoOrganizationId,
      vendor_id: vendorId,
      ...values,
      created_by: demoAdamUserId,
      updated_by: demoAdamUserId,
      row_version: 1,
      created_at: now,
      updated_at: now,
    };
    demoQuotes.unshift(quote);
    recordDemoAudit("quotes", quote.id, "INSERT", null, snapshot(quote));
    return quote;
  }

  async saveAttachmentMetadata(vendorId: string, input: AttachmentInput): Promise<CrmAttachment> {
    requireDemoVendor(vendorId);
    const now = new Date().toISOString();
    const attachment: CrmAttachment = {
      id: crypto.randomUUID(),
      organization_id: demoOrganizationId,
      vendor_id: vendorId,
      ...input,
      mime_type: input.mime_type ?? null,
      byte_size: input.byte_size ?? null,
      metadata: input.metadata ?? {},
      created_by: demoAdamUserId,
      updated_by: demoAdamUserId,
      row_version: 1,
      created_at: now,
      updated_at: now,
    };
    demoAttachments.unshift(attachment);
    recordDemoAudit("crm_attachments", attachment.id, "INSERT", null, snapshot(attachment));
    return attachment;
  }

  async refreshDedupeCandidates(vendorId?: string): Promise<number> {
    let created = 0;
    for (let firstIndex = 0; firstIndex < demoVendors.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < demoVendors.length; secondIndex += 1) {
        const first = demoVendors[firstIndex];
        const second = demoVendors[secondIndex];
        if (!first || !second || (vendorId && first.id !== vendorId && second.id !== vendorId))
          continue;
        const confidence = Math.max(
          first.abn && first.abn === second.abn ? 1 : 0,
          tokenSimilarity(first.trading_name.toLowerCase(), second.trading_name.toLowerCase()),
        );
        if (confidence < 0.62) continue;
        const [vendor, candidate] = [first, second].sort((a, b) => a.id.localeCompare(b.id));
        if (!vendor || !candidate) continue;
        if (
          demoDedupeCandidates.some(
            (item) => item.vendor_id === vendor.id && item.candidate_vendor_id === candidate.id,
          )
        )
          continue;
        const now = new Date().toISOString();
        const item: DedupeCandidate = {
          id: crypto.randomUUID(),
          organization_id: demoOrganizationId,
          vendor_id: vendor.id,
          candidate_vendor_id: candidate.id,
          candidate_vendor: candidate,
          confidence,
          reasons: { same_abn: Boolean(first.abn && first.abn === second.abn) },
          status: "pending",
          resolved_by: null,
          resolved_at: null,
          merge_target_vendor_id: null,
          created_by: demoAdamUserId,
          updated_by: demoAdamUserId,
          row_version: 1,
          created_at: now,
          updated_at: now,
        };
        demoDedupeCandidates.push(item);
        recordDemoAudit("vendor_dedupe_candidates", item.id, "INSERT", null, snapshot(item));
        created += 1;
      }
    }
    return created;
  }

  async resolveDedupeCandidate(
    id: string,
    expectedVersion: number,
    resolution: "not_duplicate" | "merge_planned",
    mergeTargetVendorId?: string,
  ): Promise<DedupeCandidate> {
    const candidate = demoDedupeCandidates.find((item) => item.id === id);
    if (!candidate) throw new Error("Duplicate candidate not found");
    assertVersion(candidate, expectedVersion);
    const before = snapshot(candidate);
    candidate.status = resolution;
    candidate.resolved_by = demoAdamUserId;
    candidate.resolved_at = new Date().toISOString();
    candidate.merge_target_vendor_id =
      resolution === "merge_planned" ? (mergeTargetVendorId ?? null) : null;
    candidate.updated_by = demoAdamUserId;
    candidate.row_version = expectedVersion + 1;
    candidate.updated_at = new Date().toISOString();
    recordDemoAudit(
      "vendor_dedupe_candidates",
      candidate.id,
      "UPDATE",
      before,
      snapshot(candidate),
    );
    return candidate;
  }

  async saveContact(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: ContactInput,
  ): Promise<Contact> {
    const existing = id ? demoContacts.find((item) => item.id === id) : null;
    if (existing) {
      if (expectedVersion === null) throw new Error("A record version is required for updates");
      assertVersion(existing, expectedVersion);
      const before = snapshot(existing);
      Object.assign(existing, {
        name: input.name,
        title: input.title || null,
        email: input.email || null,
        phone: input.phone || null,
        priority: input.priority,
        row_version: expectedVersion + 1,
        updated_at: new Date().toISOString(),
      });
      recordDemoAudit("contacts", existing.id, "UPDATE", before, snapshot(existing));
      return existing;
    }
    if (expectedVersion !== null) throw new Error("A new contact cannot have an expected version");
    if (!demoVendors.some((item) => item.id === vendorId)) throw new Error("Vendor not found");
    const now = new Date().toISOString();
    const contact: Contact = {
      id: crypto.randomUUID(),
      vendor_id: vendorId,
      name: input.name,
      title: input.title || null,
      email: input.email || null,
      phone: input.phone || null,
      priority: input.priority,
      updated_by: demoAdamUserId,
      row_version: 1,
      created_at: now,
      updated_at: now,
    };
    demoContacts.push(contact);
    recordDemoAudit("contacts", contact.id, "INSERT", null, snapshot(contact));
    return contact;
  }

  async deleteRecord(
    recordType: DeleteRecordType,
    id: string,
    expectedVersion: number,
  ): Promise<void> {
    const record = findDemoDeletableRecord(recordType, id);
    if (!record) throw new Error("Record not found");
    assertVersion(record, expectedVersion);
    let deleted = false;
    switch (recordType) {
      case "vendor":
        for (const attachment of demoAttachments.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoAttachments, attachment.id, "crm_attachments");
        }
        for (const quote of demoQuotes.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoQuotes, quote.id, "quotes");
        }
        for (const sample of demoSampleRequests.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoSampleRequests, sample.id, "sample_requests");
        }
        for (const opportunity of demoOpportunities.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoOpportunities, opportunity.id, "opportunities");
        }
        for (const candidate of demoDedupeCandidates.filter(
          (item) => item.vendor_id === id || item.candidate_vendor_id === id,
        )) {
          removeDemoRecord(demoDedupeCandidates, candidate.id, "vendor_dedupe_candidates");
        }
        for (const followUp of demoFollowUps.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoFollowUps, followUp.id, "follow_ups");
        }
        for (const order of demoOrders.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoOrders, order.id, "orders");
        }
        for (const activity of demoActivities.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoActivities, activity.id, "activities");
        }
        for (const contact of demoContacts.filter((item) => item.vendor_id === id)) {
          removeDemoRecord(demoContacts, contact.id, "contacts");
        }
        deleted = removeDemoRecord(demoVendors, id, "vendors");
        break;
      case "contact":
        deleted = removeDemoRecord(demoContacts, id, "contacts");
        break;
      case "activity":
        deleted = removeDemoRecord(demoActivities, id, "activities");
        break;
      case "order":
        deleted = removeDemoRecord(demoOrders, id, "orders");
        break;
      case "follow_up":
        deleted = removeDemoRecord(demoFollowUps, id, "follow_ups");
        break;
      case "opportunity":
        deleted = removeDemoRecord(demoOpportunities, id, "opportunities");
        break;
      case "sample_request":
        deleted = removeDemoRecord(demoSampleRequests, id, "sample_requests");
        break;
      case "quote":
        deleted = removeDemoRecord(demoQuotes, id, "quotes");
        break;
      case "attachment":
        deleted = removeDemoRecord(demoAttachments, id, "crm_attachments");
        break;
    }
    if (!deleted) throw new Error("Record not found");
  }

  async getAuditEvents(cursor: number | null = null, limit = 50) {
    const safeLimit = Math.min(Math.max(limit, 1), 100);
    const candidates = [...demoAuditEvents]
      .filter((event) => cursor === null || event.id < cursor)
      .sort((first, second) => second.id - first.id);
    const events = candidates.slice(0, safeLimit);
    return {
      events,
      nextCursor: candidates.length > safeLimit ? (events.at(-1)?.id ?? null) : null,
    };
  }

  async saveInput(input: CrmInput): Promise<Activity> {
    const existing = demoActivities.find((item) => item.client_event_id === input.client_event_id);
    if (existing) return existing;
    const now = new Date().toISOString();
    const vendor = demoVendors.find((item) => item.id === input.vendor_id);
    if (!vendor) throw new Error("Account not found");
    const vendorBefore = snapshot(vendor);
    vendor.last_activity_at = input.occurred_at;
    vendor.row_version = (vendor.row_version ?? 1) + 1;
    vendor.updated_at = now;
    recordDemoAudit("vendors", vendor.id, "UPDATE", vendorBefore, snapshot(vendor));
    const activity: Activity = {
      id: crypto.randomUUID(),
      client_event_id: input.client_event_id,
      vendor_id: input.vendor_id,
      activity_type: input.activity_type,
      occurred_at: input.occurred_at,
      summary: input.summary,
      details: input.details ?? null,
      outcome: input.outcome ?? null,
      next_action: input.next_action ?? null,
      source: input.source,
      raw_transcript: input.raw_transcript ?? null,
      channel:
        input.channel ??
        (input.activity_type === "visit"
          ? "in_person"
          : input.activity_type === "call"
            ? "phone"
            : input.activity_type === "email"
              ? "email"
              : "other"),
      direction: input.direction ?? "not_applicable",
      created_by: "20000000-0000-4000-8000-000000000001",
      author: demoAdamProfile,
      created_at: now,
      row_version: 1,
    };
    demoActivities.unshift(activity);
    recordDemoAudit("activities", activity.id, "INSERT", null, snapshot(activity));
    for (const orderInput of inputOrders(input)) {
      const order: Order = {
        id: crypto.randomUUID(),
        vendor_id: input.vendor_id,
        order_reference: null,
        description: orderInput.description,
        status: orderInput.status,
        commitment: orderInput.commitment ?? "confirmed",
        quantity: orderInput.quantity ?? null,
        unit: orderInput.unit ?? null,
        amount: orderInput.amount ?? null,
        expected_at: orderInput.expected_at ?? null,
        created_at: now,
        created_by: demoAdamUserId,
        updated_by: demoAdamUserId,
        row_version: 1,
      };
      demoOrders.unshift(order);
      recordDemoAudit("orders", order.id, "INSERT", null, snapshot(order));
    }
    for (const followUpInput of inputFollowUps(input)) {
      const followUp: FollowUp = {
        id: crypto.randomUUID(),
        vendor_id: input.vendor_id,
        due_at: followUpInput.due_at,
        action: followUpInput.action,
        status: "open",
        priority: followUpInput.priority,
        assigned_to: "20000000-0000-4000-8000-000000000001",
        snoozed_until: null,
        snooze_count: 0,
        created_by: demoAdamUserId,
        updated_by: demoAdamUserId,
        row_version: 1,
        created_at: now,
      };
      demoFollowUps.unshift(followUp);
      recordDemoAudit("follow_ups", followUp.id, "INSERT", null, snapshot(followUp));
    }
    for (const sampleInput of input.sample_requests ?? []) {
      await this.saveSampleRequest(input.vendor_id, null, null, sampleInput);
    }
    for (const quoteInput of input.quotes ?? []) {
      await this.saveQuote(input.vendor_id, null, null, quoteInput);
    }
    return activity;
  }

  async saveCaptureBundle(input: CaptureBundleInput): Promise<CaptureBundleResult> {
    const existingActivity = demoActivities.find(
      (item) => item.client_event_id === input.client_event_id,
    );
    if (existingActivity) {
      const existingVendor = demoVendors.find((item) => item.id === existingActivity.vendor_id);
      if (!existingVendor) throw new Error("Account not found");
      return { activity: existingActivity, vendor: existingVendor };
    }

    let vendor: Vendor;
    let createdVendorId: string | null = null;
    let previousVendor: Vendor | null = null;
    if (input.vendor_id) {
      if (input.contact) throw new Error("A new contact can only be added with a new account");
      const current = demoVendors.find((item) => item.id === input.vendor_id);
      if (!current) throw new Error("Account not found");
      previousVendor = { ...current };
      vendor =
        input.account && input.account.expected_version !== null
          ? await this.updateVendor(current.id, input.account.expected_version, input.account.input)
          : current;
    } else {
      if (!input.account) throw new Error("New account details are required");
      vendor = await this.createVendor(input.account.input, input.contact ?? undefined);
      createdVendorId = vendor.id;
    }

    const { account: _account, contact: _contact, ...activityInput } = input;
    try {
      const activity = await this.saveInput({
        ...activityInput,
        vendor_id: vendor.id,
      });
      return { activity, vendor };
    } catch (reason) {
      if (createdVendorId) {
        const index = demoVendors.findIndex((item) => item.id === createdVendorId);
        const contactIndexes = demoContacts.flatMap((item, index) =>
          item.vendor_id === createdVendorId ? [index] : [],
        );
        for (const contactIndex of contactIndexes.reverse()) demoContacts.splice(contactIndex, 1);
        if (index >= 0) demoVendors.splice(index, 1);
      } else if (previousVendor) {
        const index = demoVendors.findIndex((item) => item.id === previousVendor?.id);
        if (index >= 0) demoVendors[index] = previousVendor;
      }
      throw reason;
    }
  }

  async getBreakGlassExport() {
    return demoVendors.map((vendor) => ({
      vendor_id: vendor.id,
      trading_name: vendor.trading_name,
      legal_name: vendor.legal_name,
      status: vendor.status,
      abn: vendor.abn,
      location_name: vendor.location_name,
      address_line_1: vendor.address_line_1,
      address_line_2: vendor.address_line_2,
      suburb: vendor.suburb,
      state: vendor.state,
      postcode: vendor.postcode,
      email: vendor.email,
      phone: vendor.phone,
      last_activity_at: vendor.last_activity_at,
    }));
  }
}

function compareLastActivity(a: Vendor, b: Vendor): number {
  return (b.last_activity_at ?? "").localeCompare(a.last_activity_at ?? "");
}

function tokenSimilarity(first: string, second: string): number {
  if (!first || !second) return 0;
  const firstTokens = new Set(first.split(/\W+/).filter(Boolean));
  const secondTokens = new Set(second.split(/\W+/).filter(Boolean));
  const shared = [...firstTokens].filter((token) => secondTokens.has(token)).length;
  return shared / Math.max(firstTokens.size, secondTokens.size, 1);
}

function distanceBetween(
  firstLatitude: number,
  firstLongitude: number,
  secondLatitude: number,
  secondLongitude: number,
): number {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = radians(secondLatitude - firstLatitude);
  const longitudeDelta = radians(secondLongitude - firstLongitude);
  const value =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(radians(firstLatitude)) *
      Math.cos(radians(secondLatitude)) *
      Math.sin(longitudeDelta / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

function snapshot(record: object): Record<string, unknown> {
  return structuredClone(record) as Record<string, unknown>;
}

function assertVersion(record: { row_version?: number }, expectedVersion: number): void {
  if ((record.row_version ?? 1) !== expectedVersion) throw new DatabaseConflictError();
}

function requireDemoVendor(vendorId: string): Vendor {
  const vendor = demoVendors.find((item) => item.id === vendorId);
  if (!vendor) throw new Error("Account not found");
  return vendor;
}

function recordDemoAudit(
  tableName: AuditTableName,
  recordId: string,
  operation: AuditOperation,
  oldData: Record<string, unknown> | null,
  newData: Record<string, unknown> | null,
) {
  const event: AuditEvent = {
    id: Math.max(0, ...demoAuditEvents.map((item) => item.id)) + 1,
    organization_id: demoOrganizationId,
    table_name: tableName,
    record_id: recordId,
    operation,
    actor_id: demoAdamUserId,
    actor: demoAdamProfile,
    changed_at: new Date().toISOString(),
    old_data: oldData,
    new_data: newData,
  };
  demoAuditEvents.unshift(event);
}

function findDemoDeletableRecord(
  recordType: DeleteRecordType,
  id: string,
): { row_version?: number } | undefined {
  switch (recordType) {
    case "vendor":
      return demoVendors.find((item) => item.id === id);
    case "contact":
      return demoContacts.find((item) => item.id === id);
    case "activity":
      return demoActivities.find((item) => item.id === id);
    case "order":
      return demoOrders.find((item) => item.id === id);
    case "follow_up":
      return demoFollowUps.find((item) => item.id === id);
    case "opportunity":
      return demoOpportunities.find((item) => item.id === id);
    case "sample_request":
      return demoSampleRequests.find((item) => item.id === id);
    case "quote":
      return demoQuotes.find((item) => item.id === id);
    case "attachment":
      return demoAttachments.find((item) => item.id === id);
  }
}

function removeDemoRecord<T extends { id: string }>(
  records: T[],
  id: string,
  tableName: AuditTableName,
): boolean {
  const index = records.findIndex((item) => item.id === id);
  if (index < 0) return false;
  const record = records[index];
  if (!record) return false;
  records.splice(index, 1);
  recordDemoAudit(tableName, id, "DELETE", snapshot(record), null);
  return true;
}
